import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import ROUTES from '@/ROUTES'
import { isBefore } from 'date-fns'

import { IChat, IChatMessage } from '@/MODELS/chat'

import { api, api_delete, api_stream } from '@/services/api.service'

import analytics from './analytics.service'
import { useAuth } from './authentication.service'

export interface ICompletionSettings {
    temperature?: number
    json_object?: boolean
}

export function useStreamedCompletion(props?: {
    setState?: React.Dispatch<React.SetStateAction<string | null>>
    options?: ICompletionSettings
}) {
    const [stream, setStream] = useState<string | null>('')
    const [error, setError] = useState<string | null>(null)
    const [isLoading, setLoading] = useState<boolean>(false)
    const controllerRef = useRef<AbortController | null>(null)

    async function getCompletion(
        messages: Array<{ role: string; content: string }>,
        options?: ICompletionSettings
    ): Promise<string> {
        setLoading(true)
        setError(null)

        if (!controllerRef.current) {
            controllerRef.current = new AbortController()
        }

        let reader: ReadableStreamDefaultReader<Uint8Array> | null = null

        try {
            reader = await api_stream(ROUTES.app.completion, controllerRef.current.signal, {
                messages,
                options: { ...(props?.options ?? {}), ...options },
            })
        } catch (e) {
            const errorMessage = e instanceof Error ? e.message : String(e)
            setError(errorMessage)
            console.error('API Stream Error:', e)
            setLoading(false)
            throw e
        }

        if (!reader) {
            const errorMsg = 'api_stream did not return a readable stream'
            console.error(errorMsg)
            setError(errorMsg)
            setLoading(false)
            throw new Error(errorMsg)
        }

        const decoder = new TextDecoder()
        let _stream = ''

        if (props?.setState) {
            props.setState('')
        } else {
            setStream('')
        }

        try {
            while (true) {
                const { done, value: chunk } = await reader.read()

                if (done) {
                    break
                }

                try {
                    _stream += decoder.decode(chunk, { stream: true })

                    if (props?.setState) {
                        props.setState(_stream)
                    } else {
                        setStream(_stream)
                    }
                } catch (e) {
                    console.error('CHUNK DECODE ERROR:', e)
                    const errorMessage = e instanceof Error ? e.message : String(e)
                    setError(errorMessage)
                }
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === 'AbortError') {
                console.log('Stream was aborted by the user.')
                setError('Stream aborted by the user.')
            } else {
                console.error('STREAM CHUNK ERROR:', e)
                const errorMessage = e instanceof Error ? e.message : String(e)
                setError(errorMessage)
                throw e
            }
        } finally {
            setLoading(false)
        }

        return _stream
    }

    function stopCompletion() {
        if (controllerRef.current) {
            controllerRef.current.abort()
            controllerRef.current = null
        }
        setLoading(false)
    }

    return { getCompletion, stream, error, isLoading, stopCompletion }
}

// Chat Management Context Interface
export interface IChatManagementContext {
    userChatHistory: { [id: string]: IChat }
    setActiveChat: (chatId: string | null) => void
    chat: IChat
    startPresetChat: (presetMessages: IChatMessage[]) => Promise<void>
    createNewChat: () => void
    saveChat: (title: string) => Promise<void>
    deleteChat: (chatId: string) => Promise<void>
    sendMessage: (messageText: string) => Promise<void>
    streamingMessage: string | null
    stopCompletion: () => void
    isStreaming: boolean
    chatError: string | null
    clearChatError: () => void
}

const initialChatValue: IChat = {
    _id: 'new',
    messages: [],
}

const initialValue: IChatManagementContext = {
    userChatHistory: {},
    setActiveChat: () => {},
    chat: initialChatValue,
    startPresetChat: async () => {},
    createNewChat: () => {},
    saveChat: async () => {},
    deleteChat: async () => {},
    sendMessage: async () => {},
    streamingMessage: null,
    stopCompletion: () => {},
    isStreaming: false,
    chatError: null,
    clearChatError: () => {},
}

// Create Context
const ChatContext = createContext<IChatManagementContext>(initialValue)

// Chat Provider Component
export function ChatProvider({ children }: { children: React.ReactNode }) {
    const { user } = useAuth()

    const [userChatHistory, setUserChatHistory] = useState<{ [id: string]: IChat }>(initialValue.userChatHistory)
    const [chat, setChat] = useState<IChat>(initialChatValue)
    const [streamingMessage, setStreamingMessage] = useState<string | null>(initialValue.streamingMessage)
    const [chatError, setChatError] = useState<string | null>(null)

    const clearChatError = () => setChatError(null)

    const { getCompletion, stopCompletion, isLoading } = useStreamedCompletion({
        setState: setStreamingMessage,
        options: {
            temperature: 1,
        },
    })

    useEffect(() => {
        if (user) {
            fetchUserChats()
            setChat((prevChat: IChat) => ({ ...prevChat, user }))
        } else {
            setChat((prevChat: IChat) => ({ ...prevChat, user: undefined }))
        }
    }, [user])

    const fetchUserChats = async (): Promise<void> => {
        try {
            const res = await api<IChat[]>(ROUTES.app.userChatHistory)
            const chats: { [id: string]: IChat } = {}

            if (res?.data) {
                for (const chatItem of res.data) {
                    chats[chatItem._id] = chatItem
                }
            }

            setUserChatHistory(chats)
        } catch (error) {
            console.error('Failed to fetch user chats:', error)
        }
    }

    const setActiveChat = (chatId: string | null): void => {
        if (chatId && userChatHistory[chatId]) {
            setChat(userChatHistory[chatId])
        } else {
            setChat(initialChatValue)
        }
    }

    const createNewChat = (): void => {
        setChat(initialChatValue)
    }

    const saveChat = async (title: string): Promise<void> => {
        try {
            const response = await api<{ id: string }>(ROUTES.app.chat, { ...chat, title })

            if (response?.data?.id) {
                // Track chat save action
                analytics.trackChatAction('save', response.data.id)

                setChat((prevChat) => ({
                    ...prevChat,
                    title,
                    _id: response.data.id,
                }))
                await fetchUserChats()
            }
        } catch (error) {
            console.error('Failed to save chat:', error)
        }
    }

    const deleteChat = async (chatId: string): Promise<void> => {
        console.log('called')
        try {
            await api_delete(`${ROUTES.app.chat}/${chatId}`)

            // Track chat delete action
            analytics.trackChatAction('delete', chatId)

            setChat(initialChatValue)
            await fetchUserChats()
        } catch (error) {
            console.error('Failed to delete chat:', error)
        }
    }

    const _handleSetChat = async (nextState: IChat): Promise<void> => {
        try {
            if (chat._id && chat._id !== 'new') {
                const response = await api(ROUTES.app.chat, nextState)
                if (response) {
                    setStreamingMessage(null)
                    setChat((previousState) => ({ ...previousState, ...nextState }))
                    // Refresh sidebar immediately so the saved chat surfaces the moment the
                    // assistant response lands. Fire-and-forget — the 3s debounced auto-save
                    // in AIChat.tsx remains as a safety net but is no longer the only path
                    // that updates userChatHistory (which Safari can throttle in inactive tabs).
                    void fetchUserChats()
                }
            } else {
                const response = await api<{ id: string }>(ROUTES.app.chat, nextState)

                if (response?.data?.id && !response.error) {
                    setStreamingMessage(null)
                    setChat((previousState) => ({
                        ...previousState,
                        ...nextState,
                        _id: response.data.id,
                    }))
                    void fetchUserChats()
                } else {
                    console.error('Failed to create new chat:', response?.error)
                }
            }
        } catch (error) {
            console.error('Failed to update chat:', error)
        }
    }

    const sendMessage = async (text: string): Promise<void> => {
        if (!text.trim()) return

        setChatError(null)

        try {
            const messageStartTime = Date.now()

            // Track message being sent
            analytics.trackChatMessage(text.length, true)

            const newMessage: IChatMessage = {
                role: 'user',
                content: text,
                timestamp: new Date(),
            }

            const currentMessages = chat?.messages ?? []
            const updatedMessages = [...currentMessages, newMessage].sort((a, b) => {
                const dateA = new Date(a?.timestamp ?? 0)
                const dateB = new Date(b?.timestamp ?? 0)
                return isBefore(dateA, dateB) ? -1 : 1
            })

            let chatState: IChat = {
                ...chat,
                messages: updatedMessages,
            }

            setChat(chatState)

            const completeMessage = await getCompletion(chatState.messages, {
                temperature: chat?.temperature,
            })

            // Check for mid-stream error marker
            if (completeMessage.includes('[BRIGHTPATH_AI_ERROR]')) {
                setStreamingMessage(null)
                setChatError("We're experiencing a temporary issue with our AI assistant. Our team has been notified and is working to resolve it. Thank you for your patience!")
                return
            }

            const responseTime = Date.now() - messageStartTime

            // Track assistant response
            analytics.trackChatMessage(completeMessage.length, false, responseTime)

            const assistantMessage: IChatMessage = {
                role: 'assistant',
                content: completeMessage,
                timestamp: new Date(),
            }

            chatState = {
                ...chatState,
                messages: [...chatState.messages, assistantMessage],
            }

            await _handleSetChat(chatState)
        } catch (error) {
            console.error('Failed to send message:', error)
            setStreamingMessage(null)
            setChatError("We're experiencing a temporary issue with our AI assistant. Our team has been notified and is working to resolve it. Thank you for your patience!")
        }
    }

    const startPresetChat = async (presetMessages: IChatMessage[]): Promise<void> => {
        if (!presetMessages || presetMessages.length === 0) return

        setChatError(null)

        try {
            const sortedMessages = [...presetMessages].sort((a, b) => {
                const dateA = new Date(a?.timestamp ?? 0)
                const dateB = new Date(b?.timestamp ?? 0)
                return isBefore(dateA, dateB) ? -1 : 1
            })

            setChat((previousState) => ({ ...previousState, messages: sortedMessages }))

            const completeMessage = await getCompletion(sortedMessages, {
                temperature: chat?.temperature,
            })

            // Check for mid-stream error marker
            if (completeMessage.includes('[BRIGHTPATH_AI_ERROR]')) {
                setStreamingMessage(null)
                setChatError("We're experiencing a temporary issue with our AI assistant. Our team has been notified and is working to resolve it. Thank you for your patience!")
                return
            }

            setStreamingMessage(null)

            const assistantMessage: IChatMessage = {
                role: 'assistant',
                content: completeMessage,
                timestamp: new Date(),
            }

            setChat((previousState) => ({
                ...previousState,
                messages: [...(previousState?.messages ?? []), assistantMessage],
            }))
        } catch (error) {
            console.error('Failed to start preset chat:', error)
            setStreamingMessage(null)
            setChatError("We're experiencing a temporary issue with our AI assistant. Our team has been notified and is working to resolve it. Thank you for your patience!")
        }
    }

    const contextValue: IChatManagementContext = {
        userChatHistory,
        chat,
        startPresetChat,
        setActiveChat,
        sendMessage,
        streamingMessage,
        stopCompletion,
        createNewChat,
        saveChat,
        deleteChat,
        isStreaming: isLoading,
        chatError,
        clearChatError,
    }

    return <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
}

export const useChat = (): IChatManagementContext => {
    const context = useContext(ChatContext)
    if (!context) {
        throw new Error('useChat must be used within a ChatProvider')
    }
    return context
}

export const AI_DISCLAIMER_MESSAGE =
    'AI responses are advisory only, and may not always be 100% accurate. If in doubt, please seek professional medical advice.'
